import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Briefcase, Phone, Mail, MessageCircle, RefreshCcw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { api, friendlyError } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const STATUS_LABELS = {
  pendiente: { label: 'Pendiente', color: 'bg-amber-100 text-amber-800' },
  contactado: { label: 'Contactado', color: 'bg-sky-100 text-sky-800' },
  agendado: { label: 'Agendado', color: 'bg-violet-100 text-violet-800' },
  cliente: { label: 'Cliente', color: 'bg-emerald-100 text-emerald-800' },
  descartado: { label: 'Descartado', color: 'bg-gray-100 text-gray-600' },
};

export default function AgencyLeadsPanel() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.adminListAgencyLeads(filter || undefined);
      setData(r);
    } catch (e) {
      toast({ title: 'Error', description: friendlyError(e), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [filter, toast]);

  useEffect(() => { reload(); }, [reload]);

  const changeStatus = async (lead, status) => {
    try {
      await api.adminPatchAgencyLead(lead.id, { status });
      toast({ title: 'Estado actualizado', description: `${lead.company} → ${STATUS_LABELS[status].label}` });
      reload();
    } catch (e) {
      toast({ title: 'Error', description: friendlyError(e), variant: 'destructive' });
    }
  };

  if (loading && !data) return (<div className="bg-white rounded-2xl p-12 text-center" data-testid="leads-panel-loading"><Loader2 className="w-8 h-8 text-habita animate-spin mx-auto" /></div>);
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="agency-leads-panel">
      {/* Counters by status */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_LABELS).map(([key, s]) => (
          <button
            key={key}
            onClick={() => setFilter(filter === key ? '' : key)}
            data-testid={`leads-status-filter-${key}`}
            className={`text-left p-3 rounded-xl border transition-all ${filter === key ? 'border-habita ring-2 ring-habita ring-offset-1' : 'border-gray-200 hover:border-habita'}`}
          >
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s.color}`}>{s.label}</span>
            <p className="text-2xl font-bold mt-1.5 text-habita-dark">{data.counts[key] || 0}</p>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-habita" />
          {filter ? `Leads: ${STATUS_LABELS[filter].label}` : 'Todos los leads'} <span className="text-sm font-normal text-gray-500">({data.items.length})</span>
        </h3>
        <button onClick={reload} className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-300 hover:bg-habita-cream flex items-center gap-1" data-testid="leads-refresh-btn">
          <RefreshCcw className="w-3 h-3" /> Refrescar
        </button>
      </div>

      {/* Lead table */}
      {data.items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-500" data-testid="leads-empty">No hay solicitudes con este filtro todavía.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {data.items.map((lead, i) => {
            const s = STATUS_LABELS[lead.status] || STATUS_LABELS.pendiente;
            const wa = lead.whatsapp?.replace(/\D/g, '');
            return (
              <div key={lead.id} className={`p-4 ${i > 0 ? 'border-t border-gray-100' : ''}`} data-testid={`lead-row-${lead.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-habita-dark" data-testid={`lead-company-${lead.id}`}>{lead.company}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s.color}`} data-testid={`lead-status-${lead.id}`}>{s.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {lead.name} · {lead.city} · {new Date(lead.created_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-habita hover:underline"><Mail className="w-3 h-3" /> {lead.email}</a>
                      {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-habita hover:underline"><MessageCircle className="w-3 h-3" /> {lead.whatsapp}</a>}
                      <span className="text-gray-500">{lead.agents_count ?? '—'} asesores · {lead.properties_count ?? '—'} props</span>
                    </div>
                    {lead.notes && <p className="mt-2 text-xs text-gray-600 italic bg-habita-cream rounded-lg p-2 max-w-2xl">"{lead.notes}"</p>}
                  </div>
                  <select
                    value={lead.status}
                    onChange={(e) => changeStatus(lead, e.target.value)}
                    className="h-9 rounded-full border border-gray-300 px-3 text-xs font-semibold outline-none cursor-pointer focus:ring-2 focus:ring-habita"
                    data-testid={`lead-status-select-${lead.id}`}
                  >
                    {Object.entries(STATUS_LABELS).map(([k, opt]) => (
                      <option key={k} value={k}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
